import { DiagnosisTerminologyValues } from '@/generated/core-enums'
import { ClinicDateFormatValues, ClinicTimeFormatValues, ClinicNumberFormatValues } from '@/generated/core-enums'
import type * as DomainValues from '@/generated/core-enums'
import type { Direction, Locale } from '@/shared/i18n/locale'

/**
 * UI-facing session domain.
 *
 * These are the values the shell renders. They are derived from — but narrower
 * than — the generated Core wire types in `src/generated/core-api.ts`, so a
 * contract regeneration lands in `shared/api/coreClient.ts` and stops there.
 *
 * Every field is populated from the API at runtime. There is no default,
 * placeholder or sample value anywhere in `web/`: with the API removed the
 * shell renders an error, never a stand-in clinic or practitioner.
 */

export interface Clinic {
  id: string
  operatingEntity: { id: string; versionId: string; revision: number; displayName: string; country: string }
  timeZone: string
  calendarCountryCode: string
  calendarSubdivisionCode: string | null
  dateFormat: ClinicDateFormat
  timeFormat: ClinicTimeFormat
  numberFormat: ClinicNumberFormat
  diagnosisTerminology: DiagnosisTerminology | null
}

export type DiagnosisTerminology = DomainValues.DiagnosisTerminology

export const CLINIC_DATE_FORMATS = ClinicDateFormatValues
export type ClinicDateFormat = (typeof CLINIC_DATE_FORMATS)[number]

export const CLINIC_TIME_FORMATS = ClinicTimeFormatValues
export type ClinicTimeFormat = (typeof CLINIC_TIME_FORMATS)[number]

export const CLINIC_NUMBER_FORMATS = ClinicNumberFormatValues
export type ClinicNumberFormat = (typeof CLINIC_NUMBER_FORMATS)[number]

export function isDiagnosisTerminology(value: unknown): value is DiagnosisTerminology {
  return typeof value === 'string' && (DiagnosisTerminologyValues as readonly string[]).includes(value)
}

export function isClinicDateFormat(value: unknown): value is ClinicDateFormat {
  return typeof value === 'string' && (CLINIC_DATE_FORMATS as readonly string[]).includes(value)
}

export function isClinicTimeFormat(value: unknown): value is ClinicTimeFormat {
  return typeof value === 'string' && (CLINIC_TIME_FORMATS as readonly string[]).includes(value)
}

export function isClinicNumberFormat(value: unknown): value is ClinicNumberFormat {
  return typeof value === 'string' && (CLINIC_NUMBER_FORMATS as readonly string[]).includes(value)
}

export interface ClinicSettings extends Omit<Clinic, 'operatingEntity'> {
  revision: number
  updatedAt: string
}

export interface UpdateClinicSettingsInput {
  revision: number
  timeZone: string
  calendarCountryCode: string
  calendarSubdivisionCode: string | null
  dateFormat: ClinicDateFormat
  timeFormat: ClinicTimeFormat
  numberFormat: ClinicNumberFormat
}

export interface Practitioner {
  id: string
  displayName: string
}

export interface ClinicMembershipContext {
  id: string
  name: string
  country: string
  timeZone: string
}

export interface DeveloperElevation {
  id: string
  clinicId: string
  mode: DomainValues.DeveloperElevationMode
  reason: string
  activatedAt: string
  expiresAt: string | null
}

export const RELEASE_MODES = ['normal', 'verification'] as const
export type ReleaseMode = (typeof RELEASE_MODES)[number]

export function isReleaseMode(value: unknown): value is ReleaseMode {
  return typeof value === 'string' && (RELEASE_MODES as readonly string[]).includes(value)
}

export interface AppContext {
  sessionContext: import('@/shared/api/sessionContext').SessionContext
  clinic: Clinic
  practitioner: Practitioner | null
  /** Locale the workspace persists, and the direction the contract pairs with it. */
  language: Locale
  direction: Direction
  /** application capability list. An absent capability means the route does not exist. */
  capabilities: readonly string[]
  releaseMode: ReleaseMode
  availableClinics: readonly ClinicMembershipContext[]
  elevation: DeveloperElevation | null
}

export interface Identity {
  id: string
  username: string | null
  displayName: string
  personId: string | null
  accountKind: 'human' | 'support' | 'developer'
  passwordChangeRequired: boolean
  roles: readonly string[]
  capabilities: readonly string[]
  isClinicOwner: boolean
  /** The server offers this account the design surfaces: its design connections
   *  and the design draft registry. Every design read is still authorized there. */
  designAccess: boolean
}

/**
 * How the session was established. `password` is the ordinary `/login` form;
 * `telegram_mini_app` is a staff Mini App launch exchanged for a session. The
 * bridge reads it to decide whether a launch has to be exchanged again.
 */
export type SessionChannel = 'password' | 'telegram_mini_app' | 'telegram_login'

export interface Session {
  identity: Identity
  context: AppContext
  channel: SessionChannel
}

/** Authenticated account before organization selection or after membership loss. */
export interface AccountContext extends Omit<AppContext, 'clinic'> {
  clinic: Clinic | null
}

export interface AccountSession extends Omit<Session, 'context'> {
  context: AccountContext
}

export function hasOrganization(session: AccountSession | undefined): session is Session {
  return session !== undefined && session.context.clinic !== null
}
